import {
  OrganisationPurchaseRequestProgressSection,
  OrganisationPurchaseRequestSummarySection,
  OrganisationPurchaserSummarySection,
} from "@fero/components";
import {
  type OrganisationPurchaseRequestData,
  type OrganisationPurchaseRequestDetails,
  useAppService,
  useDocumentDownloaderProps,
  useEmailPanelControls,
  useGetPhysicalAddressQuery,
  useSelectedOrgId,
} from "@fero/hooks";
import { membershipData, PortalPageContent } from "@fero/layouts";
import type {
  AttributeSelection,
  PurchaseRequestAdminAction,
  PurchaseRequestFlowState,
} from "@fero/pages";
import { useTradeSalesStore } from "@fero/stores";
import { AppRoutes, getPurchaseRequestStatusChipConfig } from "@fero/utils";
import AwcOrderAttachmentWorker from "@fero/workers/awcOrderAttachment?worker";
import { LoadingButton } from "@mui/lab";
import { Alert, Stack, Tab, Tabs } from "@mui/material";
import type { NominatedPurchaserIndexItemView } from "adl-gen/ferovinum/app/api";
import type { PurchaseRequestLineItem } from "adl-gen/ferovinum/app/nompurchaser";
import { PageHeader } from "components/library/widgets/page-header";
import { Chip } from "components/widgets";
import { ButtonWithDatePickerDialog } from "components/widgets/buttons/button-with-date-picker-dialog";
import { DocumentDownloader } from "components/widgets/buttons/document-downloader";
import { LoadingActionButton } from "components/widgets/buttons/loading-action-button/loading-action-button";
import {
  EmailSourceReferenceType,
  EmailsPanel,
} from "components/widgets/email/emails";
import { useCallback, useContext, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { formatDateToLocalDate } from "utils/date-utils";
import { assertNotUndefined } from "utils/hx/util/types";
import { isAdmin } from "utils/user-utils";
import { LoggedInContext } from "../../../../../app/app";
import { CancelPurchaserRequestButton } from "./components/cancel-purchaser-request-button";

export interface OrganisationPurchaseRequestDetailsPageViewProps {
  purchaseRequestData: OrganisationPurchaseRequestDetails;
  onMarkReadyForCollection: () => Promise<void>;
  onAdminAction?: (action: PurchaseRequestAdminAction) => Promise<void>;
}

type Sections = "progress" | "order-summary" | "purchaser-summary" | "emails";
export const OrganisationPurchaseRequestDetailsPageView = ({
  purchaseRequestData,
  ...actionCallbacks
}: OrganisationPurchaseRequestDetailsPageViewProps) => {
  const service = useAppService();
  const organisationId = assertNotUndefined(useSelectedOrgId());
  const userProfile = useContext(LoggedInContext).loginState?.user?.profile;
  const [selectedTab, setSelectedTab] = useState<Sections>("progress");
  const history = useHistory();
  const { updateOptions, setPurchaseRequestId, setReservedId, setReleasedId } =
    useTradeSalesStore();
  const [loadingAmendmentData, setLoadingAmendmentData] = useState(false);
  const { data: purchaserAddress } = useGetPhysicalAddressQuery(
    purchaseRequestData.purchaserId,
    "businessAddress",
  );

  const pdfDownloaderProps = useDocumentDownloaderProps(
    purchaseRequestData.attachedDocuments,
  );
  const { showEmailPanel, authToken } = useEmailPanelControls();

  const { purchaseRequestId } = useParams<{
    purchaseRequestId: string;
  }>();

  const canEditOrCancelTradeSale = useMemo(() => {
    // Only allow editing if the user is an admin
    if (userProfile === undefined || !isAdmin(userProfile)) {
      return false;
    }

    //For now - If there is a production order, we don't allow editing
    if (purchaseRequestData.productionOrderId) {
      return false;
    }

    // Only allow editing if the purchase request has not yet been collected or delivered
    // This prevents modifications after physical transfer of goods has occurred
    const states = purchaseRequestData.stateEvents.map((event) => event.state);
    return (
      !states.includes("collected") &&
      !states.includes("delivered") &&
      !states.includes("cancelled")
    );
  }, [
    purchaseRequestData.stateEvents,
    userProfile,
    purchaseRequestData.productionOrderId,
  ]);

  const onEditRequest = useCallback(async () => {
    const isBondedSale =
      purchaseRequestData.purchaseRequestDeliveryOption.kind ===
      "collectFromStorageLocation"
        ? purchaseRequestData.purchaseRequestDeliveryOption.value
            .nominatedPurchaserCollection.bonded
        : purchaseRequestData.purchaseRequestDeliveryOption.value
            .deliveryDetails.bonded;

    setLoadingAmendmentData(true);

    const availableStock = await service.getStock({
      organisationId,
      locationId: purchaseRequestData.storageLocationId,
      holdId: purchaseRequestData.reservedId,
      releaseId: purchaseRequestData.releasedId,
    });

    setLoadingAmendmentData(false);

    updateOptions({
      isEditMode: true,
      storageLocation: purchaseRequestData.storageLocation,
    });
    setPurchaseRequestId(purchaseRequestId);
    setReservedId(purchaseRequestData.reservedId ?? undefined);
    setReleasedId(purchaseRequestData.releasedId ?? undefined);
    const flowState: PurchaseRequestFlowState<NominatedPurchaserIndexItemView> =
      {
        // TODO need to pass the delivery option in to the form
        delivery: {
          deliveryOption: purchaseRequestData.purchaseRequestDeliveryOption,
          preparationDaysType: purchaseRequestData.stockPreparationDays
            ? "days"
            : "immediate",
          preparationDays: purchaseRequestData.stockPreparationDays ?? 0,
          purchaserCoversDeliveryCost:
            purchaseRequestData.purchaserCoversDeliveryCost,
          customerPoRef: purchaseRequestData.customerPoRef ?? undefined,
          shipmentNumber: purchaseRequestData.shipmentNumber ?? undefined,
          customDeliveryCost:
            purchaseRequestData.summary.customDeliveryCost ?? undefined,
          additionalDeliveryCost:
            purchaseRequestData.summary.additionalDeliveryCost ?? undefined,
          // NB No good way to pass File back into form atm
          // deliveryInstructionDocs,
        },
        setup: {
          storageLocation: purchaseRequestData.storageLocation,
          purchaser: {
            name: purchaseRequestData.purchaserName,
            nominatedPurchaserId: purchaseRequestData.nominatedPurchaserId,
          },
          products: purchaseRequestData.products.map((p) => {
            const originalFullPricePaidUnits =
              Number(p.salePricePerPaidUnit) + Number(p.priceDiscount);
            const lineItemDiscount =
              (Number(p.priceDiscount) / originalFullPricePaidUnits) * 100;
            const productStock = availableStock.filter(
              (availableToSell) => availableToSell.productId === p.product.id,
            );
            const totalQuantity = productStock.reduce(
              (acc, available) => acc + available.quantity.stored.value,
              0,
            );
            const attributeSelection =
              (p as PurchaseRequestLineItem).attributeSelection?.reduce(
                (acc, detail) => {
                  if (detail.batchNumber !== null) {
                    acc[detail.batchNumber] = {
                      batchNumber: detail.batchNumber,
                      quantity: detail.quantity,
                      bestBefore: detail.bestBeforeDate
                        ? new Date(detail.bestBeforeDate)
                        : undefined,
                      rotationNumber: detail.rotationNumber ?? undefined,
                      rotationLine: detail.rotationLine ?? undefined,
                    };
                  }
                  return acc;
                },
                {} as AttributeSelection,
              ) || {};

            return {
              paidUnits: p.paidUnits.kind === "singles" ? p.paidUnits.value : 0,
              totalAvailable: totalQuantity, // NB we are adding the units from the original trade sale in this calculations
              totalProducible: 0, // NB: Putting 0 here for now since we won't be allowing editing of production order sales at this point
              product: p.product,
              freeUnits: p.freeUnits?.value ?? undefined,
              lineItemDiscount: isNaN(lineItemDiscount) ? 0 : lineItemDiscount,
              priceDiscount: p.priceDiscount
                ? Number(p.priceDiscount)
                : undefined,
              unitPrice: originalFullPricePaidUnits,
              attributeSelection: attributeSelection,
            };
          }),
          bondedSale: isBondedSale,
          dealDiscountAmount:
            purchaseRequestData.summary.dealDiscountAmount?.toString() ?? null,
          salePriceType: purchaseRequestData.salePriceType,
          // NB No good way to pass File back into form atm
          poFile: undefined, // Only have the PO file ID at this point
        },
        settlement: {
          advanceCashUponDelivery:
            purchaseRequestData.summary.additionalFundingRequested,
          paymentTermsPeriod: purchaseRequestData.paymentTermsPeriod,
          settlementOption: purchaseRequestData.settlementMethod,
        },
      };

    history.push(`${AppRoutes.OrganisationPurchaseRequestSetup}`, flowState);
  }, [
    history,
    purchaseRequestData,
    updateOptions,
    purchaseRequestId,
    setPurchaseRequestId,
    service,
    organisationId,
    setReservedId,
    setReleasedId,
  ]);

  return (
    <PortalPageContent
      header={
        <OrganisationPurchaseRequestDetailsHeader
          purchaseRequest={purchaseRequestData}
        />
      }
    >
      <Stack spacing={4}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Tabs value={selectedTab} onChange={(_e, v) => setSelectedTab(v)}>
            <Tab label="Progress" value="progress" />
            <Tab label="Order Summary" value="order-summary" />
            <Tab label="Purchaser Summary" value="purchaser-summary" />
            {showEmailPanel && <Tab label="Emails" value="emails" />}
          </Tabs>

          <Stack direction="row" spacing={2}>
            {canEditOrCancelTradeSale && (
              <CancelPurchaserRequestButton
                purchaseRequestId={purchaseRequestId}
              />
            )}
            {canEditOrCancelTradeSale && (
              <LoadingButton
                variant="outlined"
                onClick={onEditRequest}
                loading={loadingAmendmentData}
              >
                Edit Request
              </LoadingButton>
            )}
            <DocumentDownloader
              {...pdfDownloaderProps}
              generateAWCOrderAttachment={
                purchaseRequestData.storageLocation.warehouseGroup === "awc" &&
                actionCallbacks.onAdminAction
                  ? () => {
                      return new Promise((resolve) => {
                        const awcOrderAttachment =
                          new AwcOrderAttachmentWorker();
                        awcOrderAttachment.onmessage = (e) => {
                          if (e.data.ready === "xlsx") {
                            const a = document.createElement("a");
                            a.href =
                              "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64," +
                              e.data.data;
                            a.download = `awc_order_attachment_${
                              (
                                purchaseRequestData as OrganisationPurchaseRequestDetails
                              ).customerPoRef
                            }.xlsx`;
                            a.click();
                            resolve();
                          } else if (e.data.error) {
                            resolve();
                            throw e.data.error;
                          }
                        };
                        const purchaser = purchaseRequestData.purchaserName;
                        awcOrderAttachment.postMessage({
                          purchaseRequestData,
                          purchaser: membershipData.purchasers.find(
                            (o) => o.value.name === purchaser,
                          ),
                          address: purchaserAddress?.address,
                        });
                      });
                    }
                  : undefined
              }
            />
          </Stack>
        </Stack>

        {selectedTab === "progress" && (
          <OrganisationPurchaseRequestProgressSection
            {...purchaseRequestData}
          />
        )}

        {selectedTab === "order-summary" && (
          <OrganisationPurchaseRequestSummarySection {...purchaseRequestData} />
        )}

        {selectedTab === "purchaser-summary" && (
          <OrganisationPurchaserSummarySection
            purchaseRequestData={purchaseRequestData}
          />
        )}

        {selectedTab === "emails" && (
          <EmailsPanel
            {...{
              referenceType: EmailSourceReferenceType.PurchaseRequest,
              reference: purchaseRequestId,
              authToken: authToken,
            }}
          />
        )}

        <ActionButtons
          purchaseRequest={purchaseRequestData}
          {...actionCallbacks}
        />
      </Stack>
    </PortalPageContent>
  );
};

interface OrganisationPurchaserRequestDetailsHeaderProps {
  purchaseRequest: OrganisationPurchaseRequestData;
}

const OrganisationPurchaseRequestDetailsHeader = ({
  purchaseRequest,
}: OrganisationPurchaserRequestDetailsHeaderProps) => {
  const { stateEvents, purchaserName, purchaseRequestNumber } = purchaseRequest;
  const customerOrderNumber = purchaseRequest.summary.customerPoRef;
  const shipmentNumber = purchaseRequest.summary.shipmentNumber;
  const lastState = stateEvents[stateEvents.length - 1]?.state || "";
  const baseTitle = "Trade Sale";
  const chipConfig = getPurchaseRequestStatusChipConfig(lastState);

  const titledTexts = [
    {
      title: "Reference Number",
      text: purchaseRequestNumber,
    },
  ];
  if (customerOrderNumber) {
    titledTexts.push({
      title: "Customer Order Number",
      text: customerOrderNumber,
    });
  }
  if (shipmentNumber) {
    titledTexts.push({
      title: "Shipment Number",
      text: shipmentNumber,
    });
  }
  return (
    <PageHeader
      title={baseTitle}
      TitleRight={
        <Chip label={chipConfig.displayText} variant={chipConfig.variant} />
      }
      subtitle={`from ${purchaserName}`}
      right={{
        type: "titledText",
        titledTexts: titledTexts,
      }}
    />
  );
};

interface ActionButtonsProps {
  purchaseRequest: OrganisationPurchaseRequestData;
  onMarkReadyForCollection: () => Promise<void>;
  onAdminAction?: (action: PurchaseRequestAdminAction) => Promise<void>;
}

const ActionButtons = ({
  purchaseRequest,
  onMarkReadyForCollection,
  onAdminAction,
}: ActionButtonsProps) => {
  const { state, stateEvents, productionOrderReference } = purchaseRequest;

  // It can be marked as ready for collection if it has been accepted by the purchaser and there is no production order
  // or if the linked production order is complete
  const canBeMarkedAsReadyForCollection =
    (state === "purchaserAccepted" && productionOrderReference === undefined) ||
    state === "productionOrderComplete";
  const events = useMemo(() => {
    return {
      readyForCollectionEvent: stateEvents.find(
        (events) => events.state === "readyForCollection",
      ),
      collectedEvent: stateEvents.find(
        (events) => events.state === "collected",
      ),
      purchaserInvoicedEvent: stateEvents.find(
        (events) => events.state === "purchaserInvoiced",
      ),
    };
  }, [stateEvents]);
  const collectedDate =
    purchaseRequest.collectedDate ?? events.collectedEvent?.time;
  return (
    <Stack spacing={2} direction="row" justifyContent={"flex-end"}>
      {canBeMarkedAsReadyForCollection && (
        <LoadingActionButton onClick={onMarkReadyForCollection}>
          Mark as ready for collection
        </LoadingActionButton>
      )}

      {onAdminAction && (
        <>
          {state === "readyForCollection" && (
            <ButtonWithDatePickerDialog
              buttonLabel="Mark as collected"
              color="secondary"
              dateFieldLabel="Collected at"
              minDate={
                events.readyForCollectionEvent &&
                new Date(events.readyForCollectionEvent.time)
              }
              maxDate={new Date()}
              onConfirm={(date) =>
                onAdminAction({
                  action: "markCollected",
                  collectedAt: formatDateToLocalDate(date),
                })
              }
            />
          )}

          {state === "collected" &&
            purchaseRequest.purchaseRequestDeliveryOption.kind ===
              "deliveryToNominatedPurchaser" && (
              <ButtonWithDatePickerDialog
                buttonLabel="Mark as delivered"
                color="secondary"
                dateFieldLabel="Delivered at"
                minDate={collectedDate ? new Date(collectedDate) : undefined}
                maxDate={new Date()}
                onConfirm={(date) =>
                  onAdminAction({
                    action: "markDelivered",
                    deliveredAt: formatDateToLocalDate(date),
                  })
                }
              />
            )}

          {state === "purchaserInvoiced" && (
            <ButtonWithDatePickerDialog
              buttonLabel="Purchaser has paid"
              color="secondary"
              onConfirm={(date) =>
                onAdminAction({
                  action: "markPurchaserPaid",
                  paidAt: date,
                  fxRate: null,
                })
              }
              dialogTitle="Purchase Paid State Information"
              dateFieldLabel="When did the purchaser pay?"
              minDate={
                events.purchaserInvoicedEvent &&
                new Date(events.purchaserInvoicedEvent.time)
              }
              maxDate={new Date()}
            />
          )}

          {state === "purchaserPaid" && (
            <Alert severity="info">
              You can mark organisation as paid in Admin Portal - Treasury
            </Alert>
          )}
        </>
      )}
    </Stack>
  );
};
